<script lang="ts">
	import { onMount } from "svelte";
	import { inflectDays, inflectHours, inflectMinutes } from "@core/utils/inflect.js";
	import { DateTime, type DurationObjectUnits } from "@core/utils/luxon.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";

	// `compact` renders a small inline row (for light cards); default renders the
	// big scoreboard tiles used in the dark competition banner.
	const { target, compact = false }: { target: string; compact?: boolean } = $props();

	const targetTime = $derived(DateTime.fromISO(target));
	const { tenant } = getGlobalTemplateProps();

	function getTimeLeft(): DurationObjectUnits {
		return targetTime.diffNow(["days", "hours", "minutes"]).toObject();
	}

	let timeLeft = $state(getTimeLeft());

	function update(): void {
		timeLeft = getTimeLeft();
	}

	onMount(() => {
		const interval = setInterval(() => {
			update();
		}, 1000);

		return (): void => {
			clearInterval(interval);
		};
	});

	const digitGroups = $derived.by(() => {
		const groups: [string, number[]][] = [];
		if (timeLeft.days !== undefined) {
			groups.push([
				inflectDays[tenant.locale](timeLeft.days),
				String(Math.round(timeLeft.days)).padStart(2, "0").split("").map(Number),
			]);
		}
		if (timeLeft.hours !== undefined) {
			groups.push([
				inflectHours[tenant.locale](timeLeft.hours),
				String(Math.round(timeLeft.hours)).padStart(2, "0").split("").map(Number),
			]);
		}
		if (timeLeft.minutes !== undefined) {
			groups.push([
				inflectMinutes[tenant.locale](timeLeft.minutes),
				String(Math.round(timeLeft.minutes)).padStart(2, "0").split("").map(Number),
			]);
		}
		return groups;
	});
</script>

{#if compact}
	<div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
		{#each digitGroups as [name, digits], index (index)}
			<span class="flex items-baseline gap-1">
				<span class="tabular text-ink-900 text-sm lg:text-md xl:text-lg font-bold leading-none">{digits.join("")}</span>
				<span class="text-ink-500 text-xs font-medium">{name}</span>
			</span>
		{/each}
	</div>
{:else}
	<div class="flex gap-2 sm:gap-3 lg:gap-5">
		{#each digitGroups as [name, digits], index (index)}
			<div class="flex flex-col items-center gap-1.5 lg:gap-2">
				<div class="flex gap-1 lg:gap-2">
					{#each digits as digit, index (index)}
						<span
							class="bg-white/10 border border-white/15 text-3xl sm:text-4xl lg:text-6xl/snug p-2 sm:p-2.5 rounded-lg font-mono font-bold tabular-nums text-white min-w-10 sm:min-w-12 lg:min-w-16 xl:min-w-20 text-center backdrop-blur-sm"
							>{digit}</span
						>
					{/each}
				</div>
				<span class="eyebrow text-sand-300">{name}</span>
			</div>
		{/each}
	</div>
{/if}
